import { createContext, useContext, useEffect, useState } from "react";
import { toast } from "sonner";
import { ProductOptionsDialog } from "@/components/ProductOptionsDialog";
import { announceCartAdd } from "@/lib/usePresence";

const CartCtx = createContext(null);

// The same product can appear more than once with a different model or a
// different personalisation, so the product id alone cannot identify a line.
const lineKey = (productId, variant, customization) =>
  `${productId}::${variant || ""}::${customization || ""}`;

const load = () => {
  try {
    const stored = JSON.parse(localStorage.getItem("3duo_cart")) || [];
    // Carts saved before models and personalisation existed lack both fields.
    return stored.map((i) => ({
      variant: "",
      customization: "",
      ...i,
      key: i.key || lineKey(i.product_id, i.variant, i.customization),
    }));
  } catch {
    return [];
  }
};

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(load);
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(null);

  useEffect(() => {
    localStorage.setItem("3duo_cart", JSON.stringify(items));
  }, [items]);

  const addLine = (product, { variant = "", customization = "" } = {}) => {
    setItems((prev) => {
      const key = lineKey(product.id, variant, customization);
      const existing = prev.find((i) => i.key === key);
      if (existing) {
        return prev.map((i) => (i.key === key ? { ...i, quantity: i.quantity + 1 } : i));
      }
      // Show the chosen model's photo rather than the product cover.
      const chosen = (product.variants || []).find((v) => v.label === variant);
      return [...prev, {
        key,
        product_id: product.id,
        name: product.name,
        image: chosen?.image || product.images?.[0] || "",
        quantity: 1,
        variant,
        customization,
      }];
    });
    // Announced here rather than from a button: this is the one place every
    // route into the cart passes through, and it runs only once the item is
    // actually in - not when a dialog that can be cancelled opens.
    announceCartAdd(product.id);
    toast.success(`${product.name} adicionado ao orçamento`);
    setOpen(true);
  };

  /**
   * Entry point for the UI. `preset` carries anything already chosen on the
   * page - a model picked in the gallery, for instance - so the dialog only
   * appears when something is still missing.
   */
  const add = (product, preset = {}) => {
    if (!product) return;
    const needsVariant = (product.variants || []).length > 0 && !preset.variant;
    const needsCustomization = product.is_customizable && !preset.customization;
    if (needsVariant || needsCustomization) {
      setPending({ product, preset });
      return;
    }
    addLine(product, preset);
  };

  const remove = (key) => setItems((prev) => prev.filter((i) => i.key !== key));

  const setQty = (key, quantity) =>
    setItems((prev) => prev.map((i) => (i.key === key ? { ...i, quantity: Math.max(1, quantity) } : i)));

  const clear = () => setItems([]);
  const count = items.reduce((total, i) => total + i.quantity, 0);

  return (
    <CartCtx.Provider value={{ items, add, remove, setQty, clear, count, open, setOpen }}>
      {children}
      <ProductOptionsDialog
        product={pending?.product}
        preset={pending?.preset}
        onCancel={() => setPending(null)}
        onConfirm={(choices) => { addLine(pending.product, choices); setPending(null); }}
      />
    </CartCtx.Provider>
  );
};

export const useCart = () => useContext(CartCtx);
